import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  Building2,
  CircleDot,
  Megaphone,
  Paperclip,
  SlidersHorizontal,
  Star,
} from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { IconBtn } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import { DEFAULT_MAIL_FILTERS, mailFiltersActive, type MailFilters, type MailSort } from "./filters";

/**
 * The narrower cuts of a folder, next to the search box.
 *
 * Everything in here is a real query the list route answers, not a client-side
 * pass over the loaded page: the pane holds a hundred threads and a filter that
 * only searched those hundred would quietly lie about the rest of the mailbox.
 *
 * The menu stays open while options are toggled. Two of the three are switches
 * an operator sets together ("starred, oldest first"), and closing after each
 * one would mean reopening the menu to finish the thought.
 */
export function ThreadFilterMenu({
  filters,
  onFilters,
}: {
  filters: MailFilters;
  onFilters: (next: MailFilters) => void;
}) {
  const t = useT();
  const close = useClosePops();
  const active = mailFiltersActive(filters);
  const set = (patch: Partial<MailFilters>) => onFilters({ ...filters, ...patch });

  const sortRow = (value: MailSort, icon: typeof ArrowDownWideNarrow, label: string) => {
    const Icon = icon;
    return (
      <button
        type="button"
        className={cn("crm-mi", filters.sort === value && "is-on")}
        onClick={() => set({ sort: value })}
      >
        <Icon className="size-4" />
        {label}
      </button>
    );
  };

  return (
    <Pop
      trigger={(open) => (
        <IconBtn
          className={cn(active && "crm-cb-dot", open && "is-on")}
          title={t("mail.filter.title")}
          aria-label={t("mail.filter.title")}
          aria-expanded={open}
        >
          <SlidersHorizontal className="size-4" />
        </IconBtn>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("mail.filter.title")}</div>
        <div className="crm-menu-s">{t("mail.filter.sub")}</div>
      </div>

      <button type="button" className={cn("crm-mi", filters.unread && "is-on")} onClick={() => set({ unread: !filters.unread })}>
        <CircleDot className="size-4" />
        {t("mail.folder.unread")}
      </button>
      <button type="button" className={cn("crm-mi", filters.matched && "is-on")} onClick={() => set({ matched: !filters.matched })}>
        <Building2 className="size-4" />
        {t("mail.folder.matched")}
      </button>
      <button
        type="button"
        className={cn("crm-mi", filters.campaign && "is-on")}
        onClick={() => set({ campaign: !filters.campaign })}
      >
        <Megaphone className="size-4" />
        {t("mail.folder.campaign")}
      </button>
      <div className="crm-menu-sep" />
      <button
        type="button"
        className={cn("crm-mi", filters.starred && "is-on")}
        onClick={() => set({ starred: !filters.starred })}
      >
        <Star className={cn("size-4", filters.starred && "fill-amber-400 text-amber-400")} />
        {t("mail.filter.starred")}
      </button>
      <button
        type="button"
        className={cn("crm-mi", filters.hasAttachment && "is-on")}
        onClick={() => set({ hasAttachment: !filters.hasAttachment })}
      >
        <Paperclip className="size-4" />
        {t("mail.filter.attachment")}
      </button>

      <div className="crm-menu-sep" />
      {sortRow("newest", ArrowDownWideNarrow, t("mail.filter.newest"))}
      {sortRow("oldest", ArrowUpWideNarrow, t("mail.filter.oldest"))}

      {active ? (
        <>
          <div className="crm-menu-sep" />
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              onFilters(DEFAULT_MAIL_FILTERS);
              close();
            }}
          >
            <SlidersHorizontal className="size-4" />
            {t("mail.filter.reset")}
          </button>
        </>
      ) : null}
    </Pop>
  );
}
